import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Delete, Lock, UserPlus, Mail } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

/**
 * Full-screen PIN gate rendered when a tablet has no active POS session yet.
 * Step 1: show the eligible-server grid.
 * Step 2: PIN pad for the chosen server.
 * On success: store the token in localStorage and call ``onAuth(user)``.
 * Also exposes a "Créer mon accès" flow: the staff types their email +
 * first name, the server generates a random PIN and emails it to the
 * admin who forwards it verbally. PIN activates only after admin approves.
 */
export default function POSPinGate({ onAuth }) {
  const [servers, setServers] = useState(null);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(null);
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // New: self-onboarding modal
  const [onboarding, setOnboarding] = useState(false);
  const [onbEmail, setOnbEmail] = useState("");
  const [onbFirstName, setOnbFirstName] = useState("");
  const [onbSubmitting, setOnbSubmitting] = useState(false);
  const [onbDone, setOnbDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/pos/servers");
        setServers(data);
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || "Erreur");
      }
    })();
  }, []);

  const type = (d) => {
    if (submitting) return;
    setPin((p) => (p.length >= 4 ? p : p + d));
  };
  const backspace = () => setPin((p) => p.slice(0, -1));

  const submit = async (fullPin) => {
    if (fullPin.length !== 4 || !picked) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/pos/pin-login", {
        user_id: picked.id, pin: fullPin,
      });
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      toast.success(`Bienvenue ${data.name.split(" ")[0]}`);
      onAuth?.(data);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Erreur";
      toast.error(msg);
      setPin("");
      if (err.response?.status === 429) {
        setPicked(null); // force back to the picker on lockout
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-submit when 4 digits typed
  useEffect(() => {
    if (pin.length === 4) submit(pin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-6" data-testid="pos-pin-gate">
      <div className="w-full max-w-md">
        {!picked ? (
          <>
            <div className="text-center mb-6">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">POS · Saveurs Normandes</div>
              <h1 className="font-display text-3xl">Qui prend le service ?</h1>
              <p className="text-sm text-muted-foreground mt-2">Sélectionnez votre profil pour saisir votre PIN.</p>
            </div>

            {error && (
              <div className="rounded-xl border border-destructive/40 bg-destructive/5 text-destructive p-4 text-sm mb-4">
                {error}
              </div>
            )}

            {servers === null ? (
              <div className="flex items-center gap-2 justify-center text-muted-foreground py-12">
                <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
              </div>
            ) : servers.length === 0 ? (
              <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground text-center">
                Aucun profil staff éligible.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {servers.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => s.has_pin && setPicked(s)}
                    disabled={!s.has_pin}
                    className={`rounded-2xl border p-4 flex flex-col items-center gap-2 transition
                                ${s.has_pin ? "border-border hover:border-primary/60 hover:bg-primary/5" : "border-border/40 opacity-40 cursor-not-allowed"}`}
                    data-testid={`pos-pin-server-${s.id}`}
                    title={s.has_pin ? "Toucher pour saisir votre PIN" : "PIN non configuré — allez sur /compte"}
                  >
                    <div className="w-16 h-16 rounded-full bg-primary/15 border border-primary/40 flex items-center justify-center">
                      <span className="font-display text-2xl text-primary">{s.initials}</span>
                    </div>
                    <div className="text-sm font-medium">{s.first_name}</div>
                    {!s.has_pin && <div className="text-[10px] text-muted-foreground uppercase tracking-wider">PIN non configuré</div>}
                  </button>
                ))}
              </div>
            )}

            <div className="text-center mt-8 space-y-3">
              <Button
                variant="outline"
                onClick={() => { setOnboarding(true); setOnbDone(false); }}
                className="rounded-full"
                data-testid="pos-pin-onboard-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" /> Créer mon accès
              </Button>
              <div>
                <a href="/connexion" className="text-xs text-muted-foreground hover:text-primary" data-testid="pos-pin-fallback-login">
                  Connexion classique par email
                </a>
              </div>
            </div>
          </>
        ) : (
          <div className="rounded-3xl border border-border bg-card p-8" data-testid="pos-pin-pad">
            <button
              onClick={() => { setPicked(null); setPin(""); }}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-4"
              data-testid="pos-pin-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Retour
            </button>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 rounded-full bg-primary/15 border border-primary/40 flex items-center justify-center">
                <span className="font-display text-xl text-primary">{picked.initials}</span>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Bienvenue</div>
                <div className="font-display text-2xl">{picked.first_name}</div>
              </div>
            </div>

            <div className="flex justify-center gap-3 mb-6">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={`pin-dot-${i}`}
                  className={`w-4 h-4 rounded-full border-2 ${i < pin.length ? "bg-primary border-primary" : "border-border"}`}
                  data-testid={`pos-pin-dot-${i}`}
                />
              ))}
            </div>

            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
                <Button
                  key={d}
                  variant="outline"
                  onClick={() => type(String(d))}
                  className="h-16 text-2xl rounded-2xl font-display"
                  disabled={submitting}
                  data-testid={`pos-pin-key-${d}`}
                >
                  {d}
                </Button>
              ))}
              <div />
              <Button
                variant="outline"
                onClick={() => type("0")}
                className="h-16 text-2xl rounded-2xl font-display"
                disabled={submitting}
                data-testid="pos-pin-key-0"
              >
                0
              </Button>
              <Button
                variant="ghost"
                onClick={backspace}
                className="h-16 rounded-2xl"
                disabled={submitting || pin.length === 0}
                data-testid="pos-pin-backspace"
              >
                <Delete className="w-5 h-5" />
              </Button>
            </div>

            {submitting && (
              <div className="flex items-center justify-center gap-2 text-muted-foreground text-sm mt-4">
                <Loader2 className="w-4 h-4 animate-spin" /> Vérification…
              </div>
            )}
            <p className="text-xs text-muted-foreground text-center mt-4">
              <Lock className="w-3 h-3 inline mr-1" /> Session valable 12 h après connexion.
            </p>
          </div>
        )}
      </div>

      {/* Self-onboarding modal — creates a PIN request emailed to the admin. */}
      {onboarding && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => !onbSubmitting && setOnboarding(false)}
          data-testid="pos-pin-onboard-modal"
        >
          <div
            className="bg-card border border-border rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {onbDone ? (
              <div className="text-center space-y-4">
                <div className="mx-auto w-16 h-16 rounded-full bg-primary/15 border border-primary/40 flex items-center justify-center">
                  <Mail className="w-7 h-7 text-primary" />
                </div>
                <h2 className="font-display text-2xl">Demande envoyée</h2>
                <p className="text-sm text-muted-foreground">
                  Votre demande a été transmise à l'administration.<br />
                  Votre PIN vous sera communiqué dès qu'il aura été approuvé.
                </p>
                <Button
                  className="w-full rounded-full"
                  onClick={() => { setOnboarding(false); setOnbEmail(""); setOnbFirstName(""); }}
                  data-testid="pos-pin-onboard-done-btn"
                >
                  OK
                </Button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="mx-auto w-16 h-16 rounded-full bg-primary/15 border border-primary/40 flex items-center justify-center mb-3">
                    <UserPlus className="w-7 h-7 text-primary" />
                  </div>
                  <h2 className="font-display text-2xl">Créer mon accès POS</h2>
                  <p className="text-sm text-muted-foreground mt-2">
                    L'administration recevra une notification et vous transmettra un PIN à 4 chiffres.
                  </p>
                </div>
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (onbSubmitting) return;
                    setOnbSubmitting(true);
                    try {
                      await api.post("/auth/pos/onboard-request", {
                        email: onbEmail.trim().toLowerCase(),
                        first_name: onbFirstName.trim(),
                      });
                      setOnbDone(true);
                    } catch (err) {
                      const msg = formatApiError(err.response?.data?.detail) || "Erreur";
                      toast.error(msg);
                    } finally {
                      setOnbSubmitting(false);
                    }
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1 block">Prénom</label>
                    <Input
                      value={onbFirstName}
                      onChange={(e) => setOnbFirstName(e.target.value)}
                      required maxLength={40}
                      placeholder="Ex : Julie"
                      className="rounded-xl h-12"
                      data-testid="pos-pin-onboard-firstname"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1 block">Email</label>
                    <Input
                      type="email"
                      value={onbEmail}
                      onChange={(e) => setOnbEmail(e.target.value)}
                      required
                      placeholder="julie@saveursnormandes.fr"
                      className="rounded-xl h-12"
                      data-testid="pos-pin-onboard-email"
                    />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      type="button" variant="ghost" onClick={() => setOnboarding(false)}
                      className="flex-1 rounded-full" disabled={onbSubmitting}
                      data-testid="pos-pin-onboard-cancel-btn"
                    >
                      Annuler
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 rounded-full"
                      disabled={onbSubmitting || !onbEmail.includes("@") || !onbFirstName.trim()}
                      data-testid="pos-pin-onboard-submit-btn"
                    >
                      {onbSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Envoyer"}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
